import { useMemo } from 'react'
import type { MeetingViewModel } from './use-meeting-view-model'

/**
 * Where (and whether) the brief renders before there's a recording, decided in
 * one place so the header and body can't disagree. Each value fully
 * determines what mounts:
 * - `inline`: the brief is the whole body, flush under the header card.
 * - `get-ready`: an upcoming meeting with no brief yet. The body renders the
 *   Get Ready hero and the header suppresses its CTA row.
 * - `none`: either the meeting has a recording, so the brief lives in its tab,
 *   or there is no brief to show and no hero to show instead.
 */
export type BriefPresentation = 'inline' | 'get-ready' | 'none'

export interface MeetingBriefBody {
  /** A brief exists to show. */
  hasBrief: boolean
  presentation: BriefPresentation
}

/**
 * Header and body call this independently. They're siblings, and two cheap
 * derivations beat threading the state between them.
 */
export function useMeetingBriefBody(model: MeetingViewModel): MeetingBriefBody {
  const hasBrief = (model.brief?.brief.trim() ?? '').length > 0
  // Deliberately not `isUpcomingWithoutAudioNote`: that flag latches on the
  // first render carrying a calendar event, so a meeting whose event lands
  // after its start time reads as "not upcoming" and would take the tabbed
  // body with nothing recorded to put in it.
  const hasRecording = model.variant === 'live' || !!model.audioNoteId

  const presentation: BriefPresentation = hasRecording
    ? 'none'
    : hasBrief
      ? 'inline'
      : model.isUpcomingWithoutAudioNote
        ? 'get-ready'
        : 'none'

  return useMemo(() => ({ hasBrief, presentation }), [hasBrief, presentation])
}
